import { CHECKOUT_FEATURES } from "./constants";

const selectClass =
  "block w-64 pl-2 pr-8 py-2 border border-gray-300 rounded-md shadow-sm bg-white focus:outline-none sm:text-sm";

const priceFor = (memberships, sku) => {
  const membership = memberships.find((entry) => entry.sku === sku);
  return membership ? `$${membership.price}` : null;
};

export const CheckoutTab = ({ checkout, memberships, onChange, hasRefillPicker }) => {
  const setBaseSku = (sku) => onChange({ ...checkout, baseSku: sku || null });
  const setBaseSku90 = (sku) => onChange({ ...checkout, baseSku90: sku || null });
  // Prescription Refill is a flat two-tier price: base (all-30-day) + a 2nd base
  // (any-90-day). Only offer the 2nd base when the type has a refill picker.
  const showSecondBase = !!hasRefillPicker || !!checkout.baseSku90;

  const setFeatureSku = (key, sku) =>
    onChange({
      ...checkout,
      features: {
        ...checkout.features,
        [key]: { sku: sku || null },
      },
    });

  return (
    <div className="space-y-6 max-w-3xl">
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <h3 className="text-sm font-semibold text-gray-900">
          {showSecondBase ? "Base visit price (30-day supply)" : "Base visit price"}
        </h3>
        <p className="text-xs text-gray-500 mb-2">
          {showSecondBase
            ? "This visit is a flat two-tier price. This is charged when every selected medication is a 30-day supply. Amounts are managed in Membership Pricing."
            : "The price of this visit. Amounts are managed in Membership Pricing — pick which price item applies here, or leave it on the default (the normal visit base)."}
        </p>
        <div className="flex items-center gap-3">
          <select
            className={selectClass}
            value={checkout.baseSku || ""}
            onChange={({ target }) => setBaseSku(target.value)}
          >
            <option value="">Default price (normal visit base)</option>
            {memberships
              .filter((m) => m.product === "PRIMARY")
              .map((membership) => (
                <option key={membership.sku} value={membership.sku}>
                  {membership.sku} — {membership.name}
                </option>
              ))}
          </select>
          {checkout.baseSku && (
            <span className="text-sm font-medium text-gray-700">
              {priceFor(memberships, checkout.baseSku) || "not found / inactive"}
            </span>
          )}
        </div>
      </div>

      {showSecondBase && (
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-gray-900">Base visit price (90-day supply)</h3>
          <p className="text-xs text-gray-500 mb-2">
            Charged instead of the 30-day price the moment any selected
            medication is a 90-day supply. Leave unset to reuse the 30-day price.
          </p>
          <div className="flex items-center gap-3">
            <select
              className={selectClass}
              value={checkout.baseSku90 || ""}
              onChange={({ target }) => setBaseSku90(target.value)}
            >
              <option value="">Same as 30-day price</option>
              {memberships
                .filter((m) => m.product === "PRIMARY")
                .map((membership) => (
                  <option key={membership.sku} value={membership.sku}>
                    {membership.sku} — {membership.name}
                  </option>
                ))}
            </select>
            {checkout.baseSku90 && (
              <span className="text-sm font-medium text-gray-700">
                {priceFor(memberships, checkout.baseSku90) || "not found / inactive"}
              </span>
            )}
          </div>
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-lg divide-y divide-gray-100">
        <div className="p-4">
          <h3 className="text-sm font-semibold text-gray-900">Add-on pricing</h3>
          <p className="text-xs text-gray-500">
            Every add-on is offered as usual — pick a custom price item for
            this visit type, or leave it on the global default.
          </p>
        </div>
        {CHECKOUT_FEATURES.map((feature) => {
          const state = checkout.features[feature.key] || { sku: null };
          return (
            <div key={feature.key} className="p-4 flex items-center justify-between gap-4">
              <div>
                <div className="text-sm font-medium text-gray-900">{feature.label}</div>
                <div className="text-xs text-gray-500">{feature.description}</div>
              </div>
              <div className="flex items-center gap-3">
                <select
                  className={selectClass}
                  value={state.sku || ""}
                  onChange={({ target }) => setFeatureSku(feature.key, target.value)}
                >
                  <option value="">Default price (global)</option>
                  {memberships
                    .filter((m) => m.product === "ADD_ON")
                    .map((membership) => (
                      <option key={membership.sku} value={membership.sku}>
                        {membership.sku} — {membership.name}
                      </option>
                    ))}
                </select>
                {state.sku && (
                  <span className="text-sm font-medium text-gray-700 w-16 text-right">
                    {priceFor(memberships, state.sku) || "?"}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
